import React, { useEffect, useRef, useState } from 'react'

import './SwipeToDeleteRow.scss'

const LOCK_DISTANCE = 8

interface SwipeToDeleteRowProps {
    children: React.ReactNode
    onDelete: () => void
    deleteLabel: string
}

type Axis = 'horizontal' | 'vertical' | null

// Swiping the row to the left slides it away and shows a delete button behind it
const SwipeToDeleteRow = ({ children, onDelete, deleteLabel }: SwipeToDeleteRowProps) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const deleteRef = useRef<HTMLButtonElement>(null)
    const deleteWidth = useRef(0)
    const start = useRef({ x: 0, y: 0, offset: 0 })
    const axis = useRef<Axis>(null)
    const [offset, setOffset] = useState(0)
    const [isSwiping, setIsSwiping] = useState(false)
    const isOpen = offset < 0

    // A tap anywhere else closes the row
    useEffect(() => {
        if (!isOpen) return
        const close = (e: Event) => {
            if (!rootRef.current?.contains(e.target as Node)) setOffset(0)
        }
        document.addEventListener('touchstart', close)
        return () => document.removeEventListener('touchstart', close)
    }, [isOpen])

    // The row slides exactly as far as the button is wide, so there is no gap whatever the button's CSS says
    const getDeleteWidth = () => {
        deleteWidth.current = deleteRef.current?.offsetWidth ?? 0
        return deleteWidth.current
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        // the drag handle reorders, it does not swipe
        if ((e.target as HTMLElement).closest('.draggable-line-selection__drag-icon, .draggable-line-selection__move')) {
            axis.current = 'vertical'
            return
        }
        const touch = e.touches[0]
        start.current = { x: touch.clientX, y: touch.clientY, offset }
        axis.current = null
        getDeleteWidth()
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        if (axis.current === 'vertical') return
        const touch = e.touches[0]
        const dx = touch.clientX - start.current.x
        const dy = touch.clientY - start.current.y

        if (axis.current === null) {
            if (Math.abs(dx) < LOCK_DISTANCE && Math.abs(dy) < LOCK_DISTANCE) return
            axis.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
            if (axis.current === 'vertical') return
            setIsSwiping(true)
        }

        setOffset(Math.min(0, Math.max(-deleteWidth.current, start.current.offset + dx)))
    }

    const handleTouchEnd = () => {
        if (axis.current === 'horizontal') {
            setOffset((current) => (current < -deleteWidth.current / 2 ? -deleteWidth.current : 0))
        }
        axis.current = null
        setIsSwiping(false)
    }

    return (
        <div ref={rootRef} className="swipe-to-delete">
            <button
                ref={deleteRef}
                type="button"
                className="swipe-to-delete__delete"
                aria-label={deleteLabel}
                tabIndex={isOpen ? 0 : -1}
                onClick={onDelete}
            >
                <i className="a-icon boschicon-bosch-ic-delete" aria-hidden="true" />
            </button>
            <div
                className="swipe-to-delete__content"
                style={{
                    transform: `translateX(${offset}px)`,
                    transition: isSwiping ? 'none' : 'transform 150ms ease-out',
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {children}
            </div>
        </div>
    )
}

export default SwipeToDeleteRow
